import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import TaskForm from '../components/Forms/TaskForm';
import TaskCard from '../components/Tasks/TaskCard';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../services/NotificationService';
import { addDays, addWeeks, addMonths, addYears, format } from 'date-fns';

const { FiPlus, FiFilter, FiSearch, FiCheckSquare, FiUsers, FiClock, FiTarget, FiAlertCircle, FiList, FiLayers, FiChevronDown, FiChevronUp } = FiIcons;

// Extracted Filter Controls Component for reuse
const TaskFilterControls = ({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  assigneeFilter, setAssigneeFilter,
  priorityFilter, setPriorityFilter,
  projectFilter, setProjectFilter,
  projects,
  viewMode,
  users,
  canViewAllTasks,
  onClear
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search Tasks</label>
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={viewMode === 'project' ? "Search..." : "Search by title..."}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Projects</option>
            {projects?.map((project, index) => (
              <option key={index} value={project}>{project}</option>
            ))}
            <option value="unassigned">Unassigned</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="todo">To Do</option>
            <option value="in-progress">In Progress</option>
            <option value="review">Under Review</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {canViewAllTasks && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Assignees</option>
              {users?.filter(user => user.is_active || user.isActive).map(user => (
                <option key={user.id} value={user.id}>{user.full_name || user.fullName}</option>
              ))}
            </select>
          </div>
        )}

        <div className={!canViewAllTasks ? "md:col-span-1" : ""}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>
      
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          onClick={onClear}
          className="w-full md:w-auto"
        >
          <SafeIcon icon={FiFilter} className="h-4 w-4 mr-2" /> Clear Filters
        </Button>
      </div>
    </div>
  );
};

const Tasks = () => {
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const { users: contextUsers } = useCRM();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'project'
  
  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  
  // Filter Modal State
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [tableExists, setTableExists] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Permission Checks
  const canDeleteTask = isAdmin || currentUser?.permissions?.delete_tasks;
  const canCreateTask = isAdmin || currentUser?.permissions?.create_tasks;
  const canViewAllTasks = isAdmin || isManager;

  useEffect(() => {
    checkAndCreateTasksTable();
    loadUsers();
  }, []);

  useEffect(() => {
    if (contextUsers && contextUsers.length > 0) {
      setUsers(contextUsers);
    }
  }, [contextUsers]);

  const checkAndCreateTasksTable = async () => {
    try {
      const { data, error } = await supabase.from('tasks_crm_2025').select('id').limit(1);
      if (error && error.code === '42P01') {
        toast.error('Tasks table not found. Please contact administrator.');
        setTableExists(false);
        setLoading(false);
      } else {
        setTableExists(true);
        loadTasks();
      }
    } catch (error) {
      console.error('Error checking tasks table:', error);
      toast.error('Error initializing tasks. Please refresh the page.');
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (contextUsers && contextUsers.length > 0) {
      setUsers(contextUsers);
      return;
    }
    try {
      const { data, error } = await supabase.from('users_crm_2025').select('*').eq('is_active', true).order('full_name', { ascending: true });
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.log('Could not load users list');
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks_crm_2025')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedTasks = data?.map(task => ({
        id: task.id,
        title: task.title,
        description: task.description,
        assignedTo: task.assigned_to,
        createdBy: task.created_by,
        priority: task.priority,
        status: task.status,
        startDate: task.start_date,
        dueDate: task.due_date,
        estimatedHours: task.estimated_hours,
        projectId: task.project_id,
        tags: task.tags,
        notes: task.notes,
        attachment_url: task.attachment_url,
        attachments: task.attachments,
        recurrence_type: task.recurrence_type,
        recurrence_interval: task.recurrence_interval,
        createdAt: task.created_at,
        updatedAt: task.updated_at
      })) || [];

      setTasks(formattedTasks);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  // Get unique projects for autocomplete and filter
  const uniqueProjects = [...new Set(tasks.map(t => t.projectId).filter(Boolean))].sort();

  const handleAddTask = () => {
    if (!tableExists) {
      toast.error('Tasks table not available. Please contact your administrator.');
      return;
    }
    setIsModalOpen(true);
  };

  const uploadAttachment = async (file) => {
    if (!file) return null;
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${fileName}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('task-attachments')
        .upload(filePath, file);

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          throw new Error('Storage bucket not configured. Please contact admin.');
        }
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('task-attachments')
        .getPublicUrl(filePath);

      return { url: publicUrl, name: file.name, type: file.type || 'unknown' };
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };

  const handleSubmit = async (data) => {
    if (!tableExists) {
      toast.error('Tasks table not available. Cannot create task.');
      return;
    }
    setIsSubmitting(true);
    const toastId = toast.loading('Creating task...');

    try {
      let finalAttachments = [];
      if (data.newFiles && data.newFiles.length > 0) {
        try {
          toast.loading(`Uploading ${data.newFiles.length} files...`, { id: toastId });
          for (const file of data.newFiles) {
            const attachmentData = await uploadAttachment(file);
            if (attachmentData) {
              finalAttachments.push(attachmentData);
            }
          }
        } catch (uploadError) {
          toast.error(`One or more uploads failed: ${uploadError.message}`, { id: toastId });
          setIsSubmitting(false);
          return;
        }
      }

      const canAssign = isAdmin || isManager;
      const assignedTo = canAssign ? data.assignedTo : currentUser.id;

      const taskData = {
        title: data.title.trim(),
        description: data.description.trim(),
        assigned_to: assignedTo,
        created_by: currentUser.id,
        priority: data.priority || 'medium',
        status: data.status || 'todo',
        start_date: data.startDate,
        due_date: data.dueDate || null,
        estimated_hours: data.estimatedHours ? parseFloat(data.estimatedHours) : null,
        project_id: data.projectId || null,
        tags: data.tags || null,
        notes: data.notes || null,
        attachments: finalAttachments,
        attachment_url: finalAttachments.length > 0 ? finalAttachments[0].url : null,
        recurrence_type: data.recurrence_type || 'none',
        recurrence_interval: data.recurrence_interval || 1
      };

      const { data: newTask, error } = await supabase
        .from('tasks_crm_2025')
        .insert([taskData])
        .select()
        .single();

      if (error) throw error;

      toast.success('Task created successfully', { id: toastId });
      
      if (isAdmin || isManager) {
        const assignedUser = users.find(u => u.id === newTask.assigned_to);
        if (assignedUser && assignedUser.id !== currentUser.id) {
          await NotificationService.sendTaskAssignedEmail(newTask, assignedUser);
        }
      }

      loadTasks();
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to create task', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTask = async (taskId, data) => {
    setIsSubmitting(true);
    const toastId = toast.loading('Updating task...');

    try {
      let finalAttachments = [...(data.existingAttachments || [])];
      
      if (data.newFiles && data.newFiles.length > 0) {
        try {
          toast.loading(`Uploading ${data.newFiles.length} new files...`, { id: toastId });
          for (const file of data.newFiles) {
            const attachmentData = await uploadAttachment(file);
            if (attachmentData) {
              finalAttachments.push(attachmentData);
            }
          }
        } catch (uploadError) {
          toast.error(`Upload failed: ${uploadError.message}`, { id: toastId });
          setIsSubmitting(false);
          return;
        }
      }

      const canAssign = isAdmin || isManager;
      const assignedTo = canAssign ? data.assignedTo : currentUser.id;

      const taskData = {
        title: data.title,
        description: data.description,
        assigned_to: assignedTo,
        priority: data.priority,
        status: data.status,
        start_date: data.startDate,
        due_date: data.dueDate || null,
        estimated_hours: data.estimatedHours ? parseFloat(data.estimatedHours) : null,
        project_id: data.projectId || null,
        tags: data.tags || null,
        notes: data.notes || null,
        attachments: finalAttachments,
        attachment_url: finalAttachments.length > 0 ? finalAttachments[0].url : null,
        recurrence_type: data.recurrence_type || 'none',
        recurrence_interval: data.recurrence_interval || 1,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('tasks_crm_2025')
        .update(taskData)
        .eq('id', taskId);

      if (error) throw error;

      toast.success('Task updated successfully', { id: toastId });
      loadTasks();
      setIsModalOpen(false);
    } catch (error) {
      toast.error('Failed to update task', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canDeleteTask) {
      toast.error('You do not have permission to delete tasks.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        const { error } = await supabase.from('tasks_crm_2025').delete().eq('id', taskId);
        if (error) throw error;
        toast.success('Task deleted successfully');
        loadTasks();
      } catch (error) {
        toast.error('Failed to delete task');
      }
    }
  };

  const calculateNextDates = (startDate, dueDate, type, interval) => {
    const start = new Date(startDate);
    const due = dueDate ? new Date(dueDate) : null;
    let nextStart, nextDue;

    const addFn = type === 'daily' ? addDays :
                  type === 'weekly' ? addWeeks :
                  type === 'monthly' ? addMonths :
                  type === 'yearly' ? addYears : null;

    if (!addFn) return null;

    nextStart = addFn(start, interval);
    nextDue = due ? addFn(due, interval) : null;

    return {
      startDate: format(nextStart, 'yyyy-MM-dd'),
      dueDate: nextDue ? format(nextDue, 'yyyy-MM-dd') : null
    };
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const { data: task, error: fetchError } = await supabase
        .from('tasks_crm_2025')
        .select('*')
        .eq('id', taskId)
        .single();
        
      if (fetchError) throw fetchError;

      // Update current task status
      const { error } = await supabase
        .from('tasks_crm_2025')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (error) throw error;
      
      toast.success(`Task status updated to ${newStatus.replace('-', ' ')}`);

      // Check for recurrence and create next task if marked completed
      if (newStatus === 'completed' && task.recurrence_type && task.recurrence_type !== 'none') {
        const nextDates = calculateNextDates(
          task.start_date, 
          task.due_date, 
          task.recurrence_type, 
          task.recurrence_interval || 1
        );

        if (nextDates) {
          const newTaskData = {
            ...task,
            id: undefined, // Let DB generate new ID
            created_at: undefined,
            updated_at: undefined,
            status: 'todo',
            start_date: nextDates.startDate,
            due_date: nextDates.dueDate,
            parent_task_id: task.id // Link to previous task
          };

          const { error: recurError } = await supabase
            .from('tasks_crm_2025')
            .insert([newTaskData]);

          if (recurError) {
            console.error('Failed to create recurring task:', recurError);
            toast.error('Could not create next recurring task instance');
          } else {
            toast.success(`Next recurring task created for ${nextDates.startDate}`, {
              icon: '🔄',
              duration: 5000
            });
          }
        }
      }
      
      if (task.created_by !== currentUser.id) {
        const creator = users.find(u => u.id === task.created_by);
        if (creator) {
          await NotificationService.sendTaskStatusUpdateEmail(task, newStatus, creator, currentUser);
        }
      }
      
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task status');
      console.error(error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAssigneeFilter('all');
    setPriorityFilter('all');
    setProjectFilter('all');
  };

  const filteredTasks = tasks.filter(task => {
    // VISIBILITY FILTER:
    if (!canViewAllTasks && task.assignedTo !== currentUser.id) return false;

    if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase()) && !task.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (assigneeFilter !== 'all' && task.assignedTo !== assigneeFilter) return false;
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
    if (projectFilter !== 'all') {
      if (projectFilter === 'unassigned') {
        if (task.projectId) return false;
      } else if (task.projectId !== projectFilter) {
        return false;
      }
    }
    
    return true;
  });

  // Group tasks by project
  const tasksByProject = viewMode === 'project' 
    ? filteredTasks.reduce((groups, task) => {
        const project = task.projectId || 'Unassigned Project';
        if (!groups[project]) {
          groups[project] = [];
        }
        groups[project].push(task);
        return groups;
      }, {})
    : {};

  const stats = {
    total: filteredTasks.length,
    todo: filteredTasks.filter(t => t.status === 'todo').length,
    inProgress: filteredTasks.filter(t => t.status === 'in-progress').length,
    completed: filteredTasks.filter(t => t.status === 'completed').length,
    myTasks: tasks.filter(t => t.assignedTo === currentUser.id).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (!tableExists) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
        </div>
        <Card className="p-12 text-center border-red-200 bg-red-50">
          <SafeIcon icon={FiAlertCircle} className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Tasks Table Not Found</h3>
          <p className="text-gray-600 mb-4">The tasks table (tasks_crm_2025) doesn't exist. This needs to be created by your database administrator.</p>
          <Button onClick={checkAndCreateTasksTable} className="mt-4">Check Again</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">
          {canViewAllTasks ? 'Task Management' : 'My Tasks'}
        </h1>
        <div className="flex items-center space-x-3 self-end md:self-auto">
          {/* View Toggles */}
          <div className="flex bg-gray-200 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('list')} 
              className={`p-2 rounded-md transition-all flex items-center ${viewMode === 'list' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'}`} 
              title="List View"
            >
              <SafeIcon icon={FiList} className="h-5 w-5" />
            </button>
            <button 
              onClick={() => setViewMode('project')} 
              className={`p-2 rounded-md transition-all flex items-center ${viewMode === 'project' ? 'bg-white shadow text-primary-600' : 'text-gray-600 hover:text-gray-900'}`} 
              title="Project Group View"
            >
              <SafeIcon icon={FiLayers} className="h-5 w-5" />
            </button>
          </div>

          {canCreateTask && (
            <Button onClick={handleAddTask} disabled={isSubmitting}>
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              {canViewAllTasks ? 'Create Task' : 'Add Self Task'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50">
              <SafeIcon icon={FiCheckSquare} className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{canViewAllTasks ? 'Total Tasks' : 'My Tasks'}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-gray-50">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">To Do</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todo}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-yellow-50">
              <SafeIcon icon={FiTarget} className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50">
              <SafeIcon icon={FiCheckSquare} className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Inline Filters (Non-Sticky) - Hidden on mobile if desired, or kept as default */}
      <Card className="p-6 hidden md:block">
        <TaskFilterControls 
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
          priorityFilter={priorityFilter} setPriorityFilter={setPriorityFilter}
          projectFilter={projectFilter} setProjectFilter={setProjectFilter}
          projects={uniqueProjects}
          viewMode={viewMode}
          users={users}
          canViewAllTasks={canViewAllTasks}
          onClear={clearFilters}
        />
      </Card>

      {/* Floating Filter Button */}
      <motion.button
        className="fixed bottom-8 right-8 z-40 flex items-center bg-primary-600 text-white px-6 py-3 rounded-full shadow-xl hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        onClick={() => setIsFilterModalOpen(true)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon icon={FiFilter} className="h-5 w-5 mr-2" />
        <span className="font-semibold text-base">Filter</span>
      </motion.button>

      {/* Content Area */}
      {filteredTasks.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiCheckSquare} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {tasks.length === 0 ? 'No tasks found' : 'No tasks match your filters'}
          </h3>
          <p className="text-gray-500 mb-4">
            {tasks.length === 0 
              ? (canViewAllTasks ? 'Assign tasks to your team to get started.' : 'You have no assigned tasks.') 
              : 'Try adjusting your search criteria.'}
          </p>
          {canCreateTask && (
            <Button onClick={handleAddTask} disabled={isSubmitting}>
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              {canViewAllTasks ? 'Create Task' : 'Add Self Task'}
            </Button>
          )}
        </Card>
      ) : (
        <>
          {viewMode === 'list' ? (
            // LIST VIEW (Grid)
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredTasks.map((task, index) => (
                <motion.div 
                  key={task.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <TaskCard 
                    task={task} 
                    onEdit={handleEditTask} 
                    onDelete={handleDeleteTask} 
                    onUpdateStatus={handleUpdateStatus}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            // PROJECT GROUP VIEW
            <div className="space-y-8">
              {Object.entries(tasksByProject).sort().map(([projectName, projectTasks], groupIndex) => (
                <motion.div 
                  key={projectName}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.1 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                >
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 z-10">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg mr-3 ${projectName === 'Unassigned Project' ? 'bg-gray-200 text-gray-600' : 'bg-primary-100 text-primary-600'}`}>
                        <SafeIcon icon={FiLayers} className="h-5 w-5"/>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{projectName}</h3>
                        <p className="text-xs text-gray-500">
                          {projectTasks.filter(t => t.status === 'completed').length} / {projectTasks.length} tasks completed
                        </p>
                      </div>
                    </div>
                    <span className="bg-white border border-gray-200 text-gray-700 py-1 px-3 rounded-full text-xs font-bold shadow-sm">
                      {projectTasks.length}
                    </span>
                  </div>
                  
                  <div className="p-6 bg-gray-50/30">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {projectTasks.map(task => (
                        <TaskCard 
                          key={task.id}
                          task={task} 
                          onEdit={handleEditTask} 
                          onDelete={handleDeleteTask} 
                          onUpdateStatus={handleUpdateStatus}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Task Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { if(!isSubmitting) setIsModalOpen(false); }} 
        title={canViewAllTasks ? "Create New Task" : "Create Self Task"} 
        size="xl"
      >
        <TaskForm 
          onSubmit={handleSubmit} 
          onCancel={() => { if(!isSubmitting) setIsModalOpen(false); }} 
          availableUsers={users}
          existingProjects={uniqueProjects}
        />
      </Modal>

      {/* Filter Modal */}
      <Modal 
        isOpen={isFilterModalOpen} 
        onClose={() => setIsFilterModalOpen(false)} 
        title="Filter Tasks" 
        size="lg"
      >
        <div className="p-1">
          <TaskFilterControls 
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
            priorityFilter={priorityFilter} setPriorityFilter={setPriorityFilter}
            projectFilter={projectFilter} setProjectFilter={setProjectFilter}
            projects={uniqueProjects}
            viewMode={viewMode}
            users={users}
            canViewAllTasks={canViewAllTasks}
            onClear={clearFilters}
          />
          <div className="mt-6 flex justify-end pt-4 border-t border-gray-100">
            <Button onClick={() => setIsFilterModalOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Tasks;